import { useCallback, useEffect, useState, type FormEvent } from "react";
import { FIELD_LIMITS, type FaqEntry } from "@photography-booking/shared";
import {
  addFaq,
  listFaqs,
  removeFaq,
  updateFaq,
} from "../api/admin-client.js";
import { AdminPanel, useAdminStatus } from "./AdminPanel.js";

interface FaqAdminProps {
  token: string;
}

interface DraftState {
  question: string;
  answer: string;
}

const EMPTY_DRAFT: DraftState = { question: "", answer: "" };

/**
 * FAQ management view (Req 12.4). Lists entries and supports add
 * (`POST /admin/faq`), edit (`PUT /admin/faq`), and remove
 * (`DELETE /admin/faq`). Edits happen inline; the add form sits at the top.
 * Field lengths mirror the shared schema so the client and server agree.
 */
export function FaqAdmin({ token }: FaqAdminProps): JSX.Element {
  const [faqs, setFaqs] = useState<FaqEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DraftState>(EMPTY_DRAFT);
  const { status, reportError, reportSuccess, clearStatus } = useAdminStatus();

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await listFaqs(token);
    setLoading(false);
    if (result.ok) {
      setFaqs([...result.data].sort((a, b) => a.order - b.order));
    } else {
      reportError(result.message);
    }
  }, [token, reportError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleAdd = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    clearStatus();
    if (draft.question.trim().length === 0 || draft.answer.trim().length === 0) {
      reportError("Both a question and an answer are required.");
      return;
    }
    setBusy(true);
    const result = await addFaq(token, {
      question: draft.question.trim(),
      answer: draft.answer.trim(),
      order: faqs.length,
    });
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setDraft(EMPTY_DRAFT);
    reportSuccess("FAQ added.");
    await refresh();
  };

  const beginEdit = (entry: FaqEntry): void => {
    setEditingId(entry.faqId);
    setEditDraft({ question: entry.question, answer: entry.answer });
    clearStatus();
  };

  const handleSaveEdit = async (entry: FaqEntry): Promise<void> => {
    clearStatus();
    if (
      editDraft.question.trim().length === 0 ||
      editDraft.answer.trim().length === 0
    ) {
      reportError("Both a question and an answer are required.");
      return;
    }
    setBusy(true);
    const result = await updateFaq(token, {
      ...entry,
      question: editDraft.question.trim(),
      answer: editDraft.answer.trim(),
    });
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setEditingId(null);
    reportSuccess("FAQ updated.");
    await refresh();
  };

  const handleRemove = async (faqId: string): Promise<void> => {
    clearStatus();
    setBusy(true);
    const result = await removeFaq(token, faqId);
    setBusy(false);
    if (!result.ok) {
      reportError(result.message);
      return;
    }
    setFaqs((current) => current.filter((entry) => entry.faqId !== faqId));
    reportSuccess("FAQ removed.");
  };

  return (
    <AdminPanel
      title="FAQ"
      description="Add, edit, and remove the frequently asked questions."
      status={status}
    >
      <form className="admin-form admin-form--inset" onSubmit={handleAdd}>
        <h3 className="admin-subhead">Add a new entry</h3>
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="faq-new-question">
            Question
          </label>
          <input
            id="faq-new-question"
            className="admin-field__input"
            type="text"
            maxLength={FIELD_LIMITS.faqQuestion}
            value={draft.question}
            onChange={(event) =>
              setDraft((d) => ({ ...d, question: event.target.value }))
            }
          />
        </div>
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="faq-new-answer">
            Answer
          </label>
          <textarea
            id="faq-new-answer"
            className="admin-field__input admin-field__textarea"
            rows={3}
            maxLength={FIELD_LIMITS.faqAnswer}
            value={draft.answer}
            onChange={(event) =>
              setDraft((d) => ({ ...d, answer: event.target.value }))
            }
          />
        </div>
        <button
          type="submit"
          className="admin-button admin-button--primary"
          disabled={busy}
        >
          Add FAQ
        </button>
      </form>

      {loading ? (
        <p className="admin-empty">Loading FAQs…</p>
      ) : faqs.length === 0 ? (
        <p className="admin-empty">No FAQ entries yet.</p>
      ) : (
        <ul className="admin-list">
          {faqs.map((entry) => (
            <li key={entry.faqId} className="admin-list__row admin-list__row--stack">
              {editingId === entry.faqId ? (
                <div className="admin-form">
                  <div className="admin-field">
                    <label className="admin-field__label" htmlFor={`faq-q-${entry.faqId}`}>
                      Question
                    </label>
                    <input
                      id={`faq-q-${entry.faqId}`}
                      className="admin-field__input"
                      type="text"
                      maxLength={FIELD_LIMITS.faqQuestion}
                      value={editDraft.question}
                      onChange={(event) =>
                        setEditDraft((d) => ({ ...d, question: event.target.value }))
                      }
                    />
                  </div>
                  <div className="admin-field">
                    <label className="admin-field__label" htmlFor={`faq-a-${entry.faqId}`}>
                      Answer
                    </label>
                    <textarea
                      id={`faq-a-${entry.faqId}`}
                      className="admin-field__input admin-field__textarea"
                      rows={3}
                      maxLength={FIELD_LIMITS.faqAnswer}
                      value={editDraft.answer}
                      onChange={(event) =>
                        setEditDraft((d) => ({ ...d, answer: event.target.value }))
                      }
                    />
                  </div>
                  <div className="admin-list__actions">
                    <button
                      type="button"
                      className="admin-button admin-button--primary"
                      disabled={busy}
                      onClick={() => void handleSaveEdit(entry)}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="admin-button"
                      disabled={busy}
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="admin-list__text">
                    <p className="admin-list__label">{entry.question}</p>
                    <p className="admin-list__sub">{entry.answer}</p>
                  </div>
                  <div className="admin-list__actions">
                    <button
                      type="button"
                      className="admin-button"
                      disabled={busy}
                      onClick={() => beginEdit(entry)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="admin-button admin-button--danger"
                      disabled={busy}
                      onClick={() => void handleRemove(entry.faqId)}
                    >
                      Remove
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </AdminPanel>
  );
}
